import c from 'classnames';
import { useMemo } from 'react';
import ReactSelect, {
  type CSSObjectWithLabel,
  type Props as ReactSelectProps,
} from 'react-select';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { Typography } from '../Typography/Typography';

import styles from './Select.module.css';

// Define a generic option type
interface OptionType {
  value: string | number;
  label: string;
}

export type { CSSObjectWithLabel }; // Re-export the type

// Define props ONLY for the wrapper component
interface SelectProps {
  // Wrapper-specific props
  value?: string | number | null; // Accept primitive value or null/undefined
  onChange: (value: string | number) => void; // Expects primitive value
  options: OptionType[]; // Must provide options
  name: string;

  error?: boolean;
  enableSearch?: boolean; // Prop for the wrapper
  placeholder?: string; // Prop for the wrapper
  label?: string;
  labelInside?: boolean;
  required?: boolean;
  showRequiredIndicator?: boolean;

  // Use Omit to allow pass-through of ReactSelectProps not defined above
  // This makes 'rest' contain only the remaining valid props for ReactSelect
  [key: string]: any; // Keep this for now to avoid deeper type issues with Omit spread
  // TODO: Refine pass-through props type if possible without complex Omit usage on spread
}

export const Select = ({
  value, // Now correctly typed as string | number | null | undefined
  options,
  onChange, // Now correctly typed
  name,
  label,
  placeholder, // Destructure placeholder
  error,
  required,
  showRequiredIndicator,
  labelInside,
  enableSearch, // Destructure enableSearch
  // Capture all other props passed to the Select wrapper
  ...rest
}: SelectProps) => {
  // Emotion requires a cache key of /^[a-z-]+$/ and throws otherwise, so names that are not
  // plain letters (react-hook-form field names are often numeric ids) have to be sanitised.
  const cache = useMemo(() => {
    const derived = name.toLowerCase().replace(/_/g, '-');
    return createCache({
      key: /^[a-z-]+$/.test(derived)
        ? derived
        : `select-${derived.replace(/[^a-z-]/g, '')}`,
    });
  }, [name]);

  const userStyles = rest?.styles;

  return (
    <CacheProvider value={cache}>
      <div
        className={c(styles['select-wrapper'], {
          [styles['with-label']]: label,
          [styles['label-inside']]: labelInside,
        })}
      >
        {label && (
          <label
            className={c(styles['select-label'], {
              [styles.labelWithError]: error,
            })}
            htmlFor={`${name}-select`}
            id={`${name}-label`}
          >
            <Typography variant='body-s'>
              {label}
              {showRequiredIndicator && required && (
                <span className={styles.requiredIndicator}>*</span>
              )}
            </Typography>
          </label>
        )}
        <div className={styles.reactSelectContainer}>
          <ReactSelect
            // Explicitly pass needed props
            name={name}
            id={`${name}-select`}
            aria-labelledby={label ? `${name}-label` : undefined}
            placeholder={placeholder} // Pass destructured placeholder
            options={options}
            isSearchable={enableSearch} // Pass destructured enableSearch
            // Pass the mapped value and onChange handler
            value={options.find(option => option.value === value) ?? null}
            onChange={option => option && onChange(option.value)}
            // Ensure dropdown menu renders in a portal attached to document.body
            menuPortalTarget={
              typeof document !== 'undefined' ? document.body : null
            }
            // Add custom styles for menu portal to ensure it has a high z-index
            // and to reliably control menu + option visuals (react-select sets many
            // of these via inline styles, so CSS classes alone won't win).
            styles={{
              ...(userStyles ?? {}),
              menuPortal: (base, state) => {
                const userBase = userStyles?.menuPortal
                  ? userStyles.menuPortal(base, state)
                  : base;
                return { ...userBase, zIndex: 9999 };
              },
              menu: (base, state) => {
                const userBase = userStyles?.menu
                  ? userStyles.menu(base, state)
                  : base;
                return {
                  ...userBase,
                  borderRadius: '0.5rem',
                  overflow: 'hidden',
                  marginTop: 0,
                  marginBottom: 0,
                  border: '1px solid var(--warm-grey-300)',
                };
              },
              menuList: (base, state) => {
                const userBase = userStyles?.menuList
                  ? userStyles.menuList(base, state)
                  : base;
                return {
                  ...userBase,
                  paddingTop: 0,
                  paddingBottom: 0,
                };
              },
              option: (base, state) => {
                const userBase = userStyles?.option
                  ? userStyles.option(base, state)
                  : base;
                return {
                  ...userBase,
                  backgroundColor: state.isSelected
                    ? 'var(--warm-grey-300)'
                    : 'transparent',
                  color: 'var(--cold-grey-900)',
                  borderBottom: '1px solid var(--warm-grey-300)',
                  ':hover': {
                    backgroundColor: state.isSelected
                      ? 'var(--warm-grey-300)'
                      : 'var(--warm-grey-200)',
                  },
                  ':active': {
                    backgroundColor: 'var(--warm-grey-300)',
                  },
                };
              },
              dropdownIndicator: (base, state) => {
                const userBase = userStyles?.dropdownIndicator
                  ? userStyles.dropdownIndicator(base, state)
                  : base;
                return {
                  ...userBase,
                  transition: 'transform 150ms ease',
                  transform: state.selectProps.menuIsOpen
                    ? 'rotate(180deg)'
                    : undefined,
                  ':active': {
                    backgroundColor: 'transparent',
                  },
                };
              },
              control: (base, state) => {
                const userBase = userStyles?.control
                  ? userStyles.control(base, state)
                  : base;
                return {
                  ...userBase,
                  ':active': {
                    borderColor: 'var(--warm-grey-400)',
                  },
                };
              },
            }}
            classNames={{
              control: state =>
                c(styles.control, {
                  [styles.controlIsFocused]: state.isFocused,
                  [styles.controlWithError]: error,
                }),
              valueContainer: () =>
                c(styles.valueContainer, {
                  [styles.valueContainerLabelInside]: labelInside,
                }),
              input: () => styles.input,
              placeholder: () => styles.placeholder,
              singleValue: () => styles.singleValue,
              indicatorSeparator: () => styles.indicatorSeparator,
              menu: () => styles.menu,
              menuList: () =>
                c(styles.menuList, {
                  [styles.menuListLabelInside]: labelInside,
                }),
              // isFocused/isSelected are handled by `styles.option` above; the
              // matching CSS-module classes were dead and were removed (ENG-4834).
              option: () =>
                c(styles.option, {
                  [styles.optionLabelInside]: labelInside,
                }),
            }}
            // Pass through any other props provided to the Select wrapper
            {...(rest as Omit<
              ReactSelectProps<OptionType, false>,
              keyof SelectProps
            >)}
          />
        </div>
      </div>
    </CacheProvider>
  );
};

export default Select;
